<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { groupsApi } from '@/api/groups';

interface Props {
  modelValue: boolean;
  memberType: 'Patient' | 'Caregiver';
  memberIds: number[];
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'assigned'): void;
}>();

const queryClient = useQueryClient();
const selectedGroupId = ref<number | null>(null);
const errorMessage = ref<string | null>(null);

const { data: groups } = useQuery({
  queryKey: ['groups', 'forFilters'],
  queryFn: () => groupsApi.indexForFilters(),
  initialData: [],
});

const groupItems = computed(() =>
  (groups.value ?? []).map(g => ({ value: g.id, title: g.name })),
);

const memberCount = computed(() => props.memberIds.length);

watch(() => props.modelValue, (value) => {
  if (!value) {
    selectedGroupId.value = null;
    errorMessage.value = null;
  }
});

const { mutate: submitAssign, isPending } = useMutation({
  mutationFn: async () => {
    if (!selectedGroupId.value) return;
    const groupId = selectedGroupId.value;
    const current = await groupsApi.show(groupId);
    const patientIds = props.memberType === 'Patient' ?
      Array.from(new Set([...current.patientIds, ...props.memberIds])) :
      current.patientIds;
    const caregiverIds = props.memberType === 'Caregiver' ?
      Array.from(new Set([...current.caregiverIds, ...props.memberIds])) :
      current.caregiverIds;
    await groupsApi.update({ id: groupId, patientIds, caregiverIds });
  },
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['settings', 'groupsManagement'] });
    queryClient.invalidateQueries({ queryKey: ['groups'] });
    emit('assigned');
    emit('update:modelValue', false);
  },
  onError: () => {
    errorMessage.value = 'No se pudo asignar al grupo. Intenta de nuevo.';
  },
});

function onCancel() {
  emit('update:modelValue', false);
}

function onSubmit() {
  if (!selectedGroupId.value) {
    errorMessage.value = 'Selecciona un grupo.';
    return;
  }
  submitAssign();
}
</script>

<template>
  <v-dialog
    :model-value="modelValue"
    max-width="480"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <v-card>
      <v-card-title>Asignar a grupo</v-card-title>
      <v-card-text>
        <p class="text-body-2 mb-3">
          {{ memberCount }} {{ memberType === 'Patient' ? 'paciente(s)' : 'profesional(es)' }}
          seleccionado(s).
        </p>
        <v-select
          v-model="selectedGroupId"
          :items="groupItems"
          label="Grupo"
          variant="outlined"
          density="comfortable"
          :error-messages="errorMessage"
        />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          variant="text"
          :disabled="isPending"
          @click="onCancel"
        >
          Cancelar
        </v-btn>
        <v-btn
          color="primary"
          :loading="isPending"
          @click="onSubmit"
        >
          Agregar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
